import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ClientShell from "@/components/ClientShell";
import ErrorBoundary from "@/components/ErrorBoundary";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Declarer from "@/pages/Declarer";
import Chantier from "@/pages/Chantier";
import TicketWizard from "@/pages/TicketWizard";
import Suivi from "@/pages/Suivi";
import TicketDetail from "@/pages/TicketDetail";
import Tutos from "@/pages/Tutos";
import Vitrine from "@/pages/Vitrine";
import VitrinePublique from "@/pages/VitrinePublique";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminShell from "@/pages/admin/AdminShell";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminTickets from "@/pages/admin/AdminTickets";
import AdminTrees from "@/pages/admin/AdminTrees";
import AdminTutos from "@/pages/admin/AdminTutos";
import AdminNotices from "@/pages/admin/AdminNotices";
import AdminSavoir from "@/pages/admin/AdminSavoir";
import AdminAlertes from "@/pages/admin/AdminAlertes";
import AdminSupervision from "@/pages/admin/AdminSupervision";
import AdminMural from "@/pages/admin/AdminMural";
import AdminSondes from "@/pages/admin/AdminSondes";
import AdminVitrine from "@/pages/admin/AdminVitrine";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminClients from "@/pages/admin/AdminClients";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminDocuments from "@/pages/admin/AdminDocuments";
import AdminJournal from "@/pages/admin/AdminJournal";
import Documents from "@/pages/Documents";
import PartageDocuments from "@/pages/PartageDocuments";

const Guard = ({ role, children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-slate-400">Chargement…</div>;
  if (!user) return <Navigate to={role === "admin" ? "/admin/login" : "/login"} replace />;
  if (user.role !== role) return <Navigate to={user.role === "admin" ? "/admin" : "/"} replace />;
  return children;
};

const Routing = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/partage/:token" element={<PartageDocuments />} />
      <Route path="/realisations" element={<VitrinePublique />} />

      <Route
        element={
          <Guard role="client">
            <ClientShell />
          </Guard>
        }
      >
        <Route path="/" element={<Vitrine />} />
        <Route path="/ticket" element={<Declarer />} />
        <Route path="/declarer/:domaine" element={<TicketWizard />} />
        <Route path="/chantier/:siteId" element={<Chantier />} />
        <Route path="/suivi" element={<Suivi />} />
        <Route path="/suivi/:id" element={<TicketDetail />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/tutos" element={<Tutos />} />
        <Route path="/vitrine" element={<Navigate to="/" replace />} />
      </Route>

      <Route
        path="/admin"
        element={
          <Guard role="admin">
            <AdminShell />
          </Guard>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="tickets" element={<AdminTickets />} />
        <Route path="arbres" element={<AdminTrees />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="documents" element={<AdminDocuments />} />
        <Route path="journal" element={<AdminJournal />} />
        <Route path="utilisateurs" element={<AdminUsers />} />
        <Route path="tutos" element={<AdminTutos />} />
        <Route path="notices" element={<AdminNotices />} />
        <Route path="savoir" element={<AdminSavoir />} />
        <Route path="alertes" element={<AdminAlertes />} />
        <Route path="supervision" element={<AdminSupervision />} />
        <Route path="sondes" element={<AdminSondes />} />
        <Route path="vitrine" element={<AdminVitrine />} />
        <Route path="reglages" element={<AdminSettings />} />
      </Route>

      <Route
        path="/admin/mural"
        element={
          <Guard role="admin">
            <AdminMural />
          </Guard>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routing />
            <Toaster position="top-center" />
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
